import type { AssetType, TransactionStatus, FixedAssetGroup } from "@/features/accounting/warehouse";
import type { PurchaseStatus, PurchaseCategory, UnitOfMeasurement } from "./domain";

export interface InvoiceListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: PurchaseListItem[];
}

export interface PurchaseListItem {
  id: number;
  internal_id: string;
  invoice_number: string;
  invoice_date: string;
  sf_number?: string | null;
  sf_date?: string | null;
  vendor_name: string;
  vendor_bin: string;
  transaction_status: TransactionStatus;
  transaction_status_display: string;
  line_items_count: number;
  units_count: number;
  subtotal: string;
  vat_amount: string;
  total_amount: string;
  created_at: string;
  category?: PurchaseCategory | "mixed";
}

export function assetTypeToCategory(assetType: AssetType): PurchaseCategory {
  switch (assetType) {
    case "inventory":
      return "1330";
    case "fixed_asset":
      return "2410";
    case "intangible":
      return "2730";
    default:
      return "1330";
  }
}

export function transactionStatusToPurchaseStatus(status: TransactionStatus): PurchaseStatus {
  return status as PurchaseStatus;
}

export function getInvoiceCategory(detail: InvoiceDetailResponse): PurchaseCategory | "mixed" {
  const categories = detail.line_items.map((item) => assetTypeToCategory(item.asset_type));
  const uniqueCategories = Array.from(new Set(categories));
  return uniqueCategories.length === 1 ? uniqueCategories[0] : "mixed";
}

export interface InvoiceVendorSerializer {
  id: number;
  name: string;
  bin: string;
  address?: string;
}

export interface InvoiceUnitSerializer {
  id: number;
  barcode: string;
  serial_number: string;
  status: string;
  status_display: string;
  unit_cost: string;
  residual_value: string;
}

export interface InvoiceLineItemWithUnitsSerializer {
  id: number;
  name: string;
  description?: string;
  asset_type: AssetType;
  asset_type_display: string;
  account_code: string;
  fixed_asset_group?: FixedAssetGroup | null;
  fixed_asset_group_display?: string | null;
  quantity: number;
  unit: UnitOfMeasurement;
  unit_display: string;
  unit_price: string;
  vat_rate: string | null;
  net_amount: string;
  vat_amount: string;
  total_cost: string;
  useful_life_months?: number | null;
  depreciation_rate?: string | null;
  units_count: number;
  units: InvoiceUnitSerializer[];
}

export interface InvoiceDetailResponse {
  id: number;
  internal_id: string;
  invoice_number: string;
  invoice_date: string;
  sf_number?: string | null;
  sf_date?: string | null;
  receipt_date: string;
  vendor: InvoiceVendorSerializer;
  transaction_status: TransactionStatus;
  transaction_status_display: string;
  subtotal: string;
  vat_amount: string;
  total_amount: string;
  current_cash_balance: string;
  line_items: InvoiceLineItemWithUnitsSerializer[];
  created_by: number;
  created_by_name: string;
  created_at: string;
  approved_by?: number | null;
  approved_by_name?: string | null;
  approved_at?: string | null;
  paid_by?: number | null;
  paid_by_name?: string | null;
  paid_at?: string | null;
  updated_at: string;
}

export type PurchaseDetailResponse = InvoiceDetailResponse;

export type PurchaseItem = InvoiceLineItemWithUnitsSerializer;

export interface JournalEntry {
  id: number;
  debit_account: string;
  credit_account: string;
  amount: string;
  description: string;
  entry_date: string;
}

export interface CreatePurchaseDto {
  vendor_id?: number;
  vendor_name?: string;
  vendor_bin?: string;
  vendor_address?: string;
  invoice_number?: string;
  invoice_date?: string;
  sf_number?: string;
  sf_date?: string;
  line_items: CreatePurchaseItemDto[];
}

export type UpdatePurchaseDto = Partial<CreatePurchaseDto>;

export interface CreatePurchaseItemDto {
  name: string;
  description?: string;
  asset_type: AssetType;
  unit_price: string;
  fixed_asset_group?: FixedAssetGroup;
  quantity?: number;
  unit?: UnitOfMeasurement;
  vat_rate?: string | null;
  useful_life_months?: number;
  depreciation_rate?: string;
}

export interface ApprovePurchaseDto {
  id: number;
}

export interface MarkPaidPurchaseDto {
  id: number;
  paid_date?: string;
}

